import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { getProductInventory } from '../../data/mockData';

export function ProductInventory() {
  const inventory = getProductInventory();

  const statusConfig = {
    healthy: {
      label: 'Healthy',
      badgeClass: 'bg-emerald-500/15 text-emerald-500 border-emerald-500/20',
      progressClass: 'bg-emerald-500',
    },
    low: {
      label: 'Low',
      badgeClass: 'bg-amber-500/15 text-amber-500 border-amber-500/20',
      progressClass: 'bg-amber-500',
    },
    critical: {
      label: 'Critical',
      badgeClass: 'bg-rose-500/15 text-rose-500 border-rose-500/20',
      progressClass: 'bg-rose-500',
    },
  };

  const categoryIcons: Record<string, string> = {
    'raw-material': '⚡',
    component: '🔧',
    packaging: '📦',
  };

  const categoryGradients: Record<string, string> = {
    'raw-material': 'from-violet-500/20 to-indigo-500/10',
    component: 'from-cyan-500/20 to-blue-500/10',
    packaging: 'from-amber-500/20 to-orange-500/10',
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg font-semibold">
          Product Inventory
        </CardTitle>
        <div className="flex gap-2">
          <Button variant="secondary" size="sm" className="text-xs">
            All
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-xs text-muted-foreground"
          >
            Low Stock
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-xs text-muted-foreground"
          >
            Critical
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {inventory.map((inv) => {
            const config = statusConfig[inv.status];
            const category = inv.item?.category ?? 'raw-material';
            const icon = categoryIcons[category] ?? '📦';
            const gradient =
              categoryGradients[category] ?? categoryGradients['raw-material'];

            return (
              <div
                key={inv.id}
                className="group bg-muted/50 rounded-xl p-5 border border-border hover:border-violet-500/30 transition-all duration-300 cursor-pointer hover:shadow-lg hover:-translate-y-0.5"
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div
                    className={`w-12 h-12 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center text-2xl`}
                  >
                    {icon}
                  </div>
                  <Badge
                    variant="outline"
                    className={`text-[10px] font-semibold uppercase tracking-wide ${config.badgeClass}`}
                  >
                    {config.label}
                  </Badge>
                </div>

                {/* Product Info */}
                <h4 className="font-semibold text-sm mb-1 group-hover:text-violet-400 transition-colors">
                  {inv.item?.name ?? 'Unknown Item'}
                </h4>
                <p className="text-xs font-mono text-muted-foreground mb-4">
                  SKU: {inv.item?.sku ?? 'N/A'}
                </p>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="bg-background/60 rounded-lg p-3">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">
                      In Stock
                    </p>
                    <p className="font-mono font-semibold text-sm">
                      {inv.currentStock.toLocaleString()}
                    </p>
                  </div>
                  <div className="bg-background/60 rounded-lg p-3">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">
                      Min Level
                    </p>
                    <p className="font-mono font-semibold text-sm">
                      {inv.reorderPoint.toLocaleString()}
                    </p>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="h-1 bg-background/60 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${config.progressClass}`}
                    style={{ width: `${inv.stockPercentage}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
